import React, { useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import { getFloatingPositionClasses, getFloatingOffsetStyles, useSmartPosition } from '../hooks';
import type { FloatingPosition, FloatingAlignment, FloatingOffset } from '../hooks';
import { Tooltip } from '../Tooltip';
import type { TooltipConfig } from '../hooks/useTooltip';
import './Dropdown.scss';

export type DropdownPosition = FloatingPosition;
export type DropdownAlignment = FloatingAlignment;
export type DropdownItemAlignment = 'left' | 'center' | 'right' | 'between';
export type DropdownOffset = FloatingOffset;

export interface DropdownItemConfig {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  divider?: boolean;
  className?: string;
  /** Nested submenu items - renders as flyout to the right */
  submenu?: DropdownItemConfig[];
}

export interface DropdownProps {
  /** The trigger element that opens the dropdown */
  trigger: ReactNode;
  /** Array of dropdown item configurations */
  items?: DropdownItemConfig[];
  /** Custom content to render instead of items */
  children?: ReactNode;
  /** Position relative to trigger */
  position?: DropdownPosition;
  /** Alignment of dropdown relative to trigger */
  alignment?: DropdownAlignment;
  /** Alignment of content within items */
  itemAlignment?: DropdownItemAlignment;
  /** Offset from calculated position */
  offset?: DropdownOffset;
  /** Whether dropdown is open (controlled mode) */
  isOpen?: boolean;
  /** Callback when dropdown open state changes (controlled mode) */
  onOpenChange?: (isOpen: boolean) => void;
  /** Close dropdown when an item is clicked */
  closeOnItemClick?: boolean;
  /** Close dropdown when clicking outside */
  closeOnClickOutside?: boolean;
  /** Enable smart positioning (auto-flip to stay in viewport) */
  enableSmartPosition?: boolean;
  /** Additional className for dropdown container */
  className?: string;
  /** Additional className for dropdown menu */
  menuClassName?: string;
  /** Minimum width for dropdown menu */
  minWidth?: number;
  /** Maximum height for dropdown menu (scrollable if exceeded) */
  maxHeight?: number;
  /** Tooltip for the trigger element */
  tooltip?: string | TooltipConfig;
  /** Render dropdown menu in a portal (for use inside overflow:hidden containers) */
  usePortal?: boolean;
}

/**
 * Flexible dropdown component that can be attached to any trigger element
 * Supports custom positioning, alignment, and content
 */
export const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  children,
  position = 'bottom',
  alignment = 'end',
  itemAlignment = 'left',
  offset = { x: 0, y: 0 },
  isOpen: controlledIsOpen,
  onOpenChange,
  closeOnItemClick = true,
  closeOnClickOutside = true,
  enableSmartPosition = true,
  className,
  menuClassName,
  minWidth,
  maxHeight,
  tooltip,
  usePortal = false,
}) => {
  const prefix = prefixClass('ksq2-dropdown');
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [menuSize, setMenuSize] = useState({ width: 200, height: 100 });
  const [activeSubmenuId, setActiveSubmenuId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use controlled or uncontrolled state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = (open: boolean) => {
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(open);
    }
    onOpenChange?.(open);
  };

  // Handle click outside
  useEffect(() => {
    if (!closeOnClickOutside || !isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is inside the trigger/container
      const isInsideDropdown = dropdownRef.current?.contains(target);
      // Also check if click is inside the portal menu (when using portal)
      const isInsideMenu = menuRef.current?.contains(target);

      if (!isInsideDropdown && !isInsideMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeOnClickOutside]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Measure menu size when it opens
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      setMenuSize({ width: rect.width, height: rect.height });
    }
  }, [isOpen, items, children]);

  // Get trigger rect for smart positioning
  const getTriggerRect = (): DOMRect | null => {
    if (!dropdownRef.current) return null;
    const triggerElement = dropdownRef.current.querySelector(`.${prefix('trigger')}`);
    return triggerElement?.getBoundingClientRect() || null;
  };

  // Use smart positioning
  const smartPosition = useSmartPosition({
    position,
    alignment,
    triggerRect: isOpen ? getTriggerRect() : null,
    contentWidth: menuSize.width,
    contentHeight: menuSize.height,
    gap: 4,
    enableFlip: enableSmartPosition,
  });

  const handleTriggerClick = () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item: DropdownItemConfig) => {
    if (item.disabled) return;

    item.onClick();

    if (closeOnItemClick) {
      setIsOpen(false);
    }
  };

  const getPositionClasses = () => {
    const finalPosition = smartPosition.position;
    const finalAlignment = smartPosition.alignment;
    const floatingClasses = getFloatingPositionClasses(prefix('menu'), finalPosition, finalAlignment);
    return classNames(prefix('menu'), ...floatingClasses, menuClassName);
  };

  const getMenuStyle = (): React.CSSProperties => {
    const style: React.CSSProperties = {};

    // Size constraints
    if (minWidth) style.minWidth = `${minWidth}px`;
    if (maxHeight) style.maxHeight = `${maxHeight}px`;

    // Apply offset using shared utility
    const offsetStyles = getFloatingOffsetStyles(offset);
    Object.assign(style, offsetStyles);

    return style;
  };

  // Get portal menu style with absolute positioning
  const getPortalMenuStyle = (): React.CSSProperties => {
    const triggerRect = getTriggerRect();
    if (!triggerRect) return {};

    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
    };

    // Size constraints
    if (minWidth) style.minWidth = `${minWidth}px`;
    if (maxHeight) style.maxHeight = `${maxHeight}px`;

    // Calculate position based on trigger rect
    const finalPosition = smartPosition.position;
    const finalAlignment = smartPosition.alignment;

    // Vertical positioning
    if (finalPosition === 'bottom') {
      style.top = triggerRect.bottom + (offset.y || 0) + 4;
    } else if (finalPosition === 'top') {
      style.bottom = window.innerHeight - triggerRect.top + (offset.y || 0) + 4;
    }

    // Horizontal alignment
    if (finalAlignment === 'end') {
      style.right = window.innerWidth - triggerRect.right + (offset.x || 0);
    } else if (finalAlignment === 'start') {
      style.left = triggerRect.left + (offset.x || 0);
    } else {
      // center
      style.left = triggerRect.left + triggerRect.width / 2 + (offset.x || 0);
      style.transform = 'translateX(-50%)';
    }

    return style;
  };

  // Handle submenu hover with delay to prevent flickering
  const handleSubmenuEnter = useCallback((itemId: string) => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
    setActiveSubmenuId(itemId);
  }, []);

  const handleSubmenuLeave = useCallback(() => {
    submenuTimeoutRef.current = setTimeout(() => {
      setActiveSubmenuId(null);
    }, 150);
  }, []);

  // Reset submenu when main dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setActiveSubmenuId(null);
    }
  }, [isOpen]);

  const renderDefaultItems = () => {
    if (!items || items.length === 0) return null;

    // Check if any item has an icon
    const hasAnyIcon = items.some(item => !item.divider && item.icon);

    return items.map((item, index) => {
      if (item.divider) {
        return <div key={`divider-${index}`} className={prefix('divider')} />;
      }

      const hasSubmenu = item.submenu && item.submenu.length > 0;

      return (
        <div
          key={item.id}
          className={classNames(prefix('item-wrapper'), {
            [prefix('item-wrapper--has-submenu')]: hasSubmenu,
          })}
          onMouseEnter={hasSubmenu ? () => handleSubmenuEnter(item.id) : undefined}
          onMouseLeave={hasSubmenu ? handleSubmenuLeave : undefined}
        >
          <button
            className={classNames(
              prefix('item'),
              prefix(`item--align-${itemAlignment}`),
              {
                [prefix('item--disabled')]: item.disabled,
                [prefix('item--danger')]: item.danger,
                [prefix('item--no-icons')]: !hasAnyIcon,
                [prefix('item--has-submenu')]: hasSubmenu,
              },
              item.className
            )}
            onClick={() => (hasSubmenu ? handleSubmenuEnter(item.id) : handleItemClick(item))}
            disabled={item.disabled}
            type="button"
          >
            {itemAlignment === 'right' ? (
              <>
                <span className={prefix('item-label')}>{item.label}</span>
                {hasAnyIcon && <span className={prefix('item-icon')}>{item.icon}</span>}
              </>
            ) : (
              <>
                {hasAnyIcon && <span className={prefix('item-icon')}>{item.icon}</span>}
                <span className={prefix('item-label')}>{item.label}</span>
              </>
            )}
            {hasSubmenu && <span className={prefix('item-chevron')}>▸</span>}
          </button>

          {/* Submenu */}
          {hasSubmenu && activeSubmenuId === item.id && (
            <div
              className={prefix('submenu')}
              onMouseEnter={() => handleSubmenuEnter(item.id)}
              onMouseLeave={handleSubmenuLeave}
            >
              {item.submenu!.map(subItem => (
                <button
                  key={subItem.id}
                  className={classNames(
                    prefix('item'),
                    prefix(`item--align-${itemAlignment}`),
                    {
                      [prefix('item--disabled')]: subItem.disabled,
                      [prefix('item--danger')]: subItem.danger,
                    },
                    subItem.className
                  )}
                  onClick={() => {
                    subItem.onClick();
                    if (closeOnItemClick) {
                      setIsOpen(false);
                    }
                  }}
                  disabled={subItem.disabled}
                  type="button"
                >
                  {subItem.icon && <span className={prefix('item-icon')}>{subItem.icon}</span>}
                  <span className={prefix('item-label')}>{subItem.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  const renderTrigger = () => {
    const triggerElement = (
      <div className={prefix('trigger')} onClick={handleTriggerClick}>
        {trigger}
      </div>
    );

    if (tooltip && !isOpen) {
      const tooltipConfig =
        typeof tooltip === 'string'
          ? { content: tooltip, position: 'top' as const, alignment: 'center' as const }
          : tooltip;
      return <Tooltip {...tooltipConfig}>{triggerElement}</Tooltip>;
    }

    return triggerElement;
  };

  const renderMenu = () => {
    if (!isOpen) return null;

    const menuContent = (
      <div ref={menuRef} className={getPositionClasses()} style={usePortal ? getPortalMenuStyle() : getMenuStyle()}>
        {children || renderDefaultItems()}
      </div>
    );

    if (usePortal) {
      return createPortal(menuContent, document.body);
    }

    return menuContent;
  };

  return (
    <div ref={dropdownRef} className={classNames(prefix(), className)}>
      {renderTrigger()}
      {renderMenu()}
    </div>
  );
};

export default Dropdown;
